import { useState, useCallback, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Upload, FileText, Calendar, Download, CheckCircle, AlertCircle, AlertTriangle, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [originalFilename, setOriginalFilename] = useState('');
  const [xmlPreview, setXmlPreview] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [downloadFilename, setDownloadFilename] = useState('');
  const [validation, setValidation] = useState(null);
  const [bt13History, setBt13History] = useState([]);
  
  // Form fields
  const [dateType, setDateType] = useState('period'); // 'period' or 'delivery'
  const [bt72, setBt72] = useState('');
  const [bt73, setBt73] = useState('');
  const [bt74, setBt74] = useState('');
  const [bt13, setBt13] = useState('');

  const fetchBt13History = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bt13-history`);
      setBt13History(res.data?.suggestions || []);
    } catch (err) {
      console.warn('BT-13 history fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchBt13History();
  }, [fetchBt13History]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const uploadFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Bitte nur PDF-Dateien hochladen');
      return;
    }

    setIsProcessing(true);
    setIsUpdated(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Upload successful, file_id:', response.data.file_id);
      setFileId(response.data.file_id);
      setOriginalFilename(response.data.original_filename);
      setXmlPreview(response.data.xml_preview);
      setUploadedFile(file);
      toast.success('PDF erfolgreich hochgeladen');
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen der Datei');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!fileId) {
      toast.error('Bitte zuerst eine Datei hochladen');
      return;
    }

    // Validate: at least date OR bt_13 must be provided
    if (dateType === 'delivery' && !bt72 && !bt13) {
      toast.error('Bitte Lieferdatum oder Bestellreferenz eingeben');
      return;
    }
    if (dateType === 'period' && (!bt73 || !bt74) && !bt13) {
      if (!bt73 && !bt74) {
        // ok if only bt_13 set
      } else {
        toast.error('Bitte beide Daten für den Leistungszeitraum eingeben');
        return;
      }
    }
    if (dateType === 'period' && bt73 && !bt74) {
      toast.error('Bitte beide Daten für den Leistungszeitraum eingeben');
      return;
    }
    if (dateType === 'period' && !bt73 && bt74) {
      toast.error('Bitte beide Daten für den Leistungszeitraum eingeben');
      return;
    }

    // Prevent double clicks
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log('Updating invoice with file_id:', fileId);
      
      const response = await axios.post(`${API}/update-invoice`, {
        file_id: fileId,
        bt_72: dateType === 'delivery' ? bt72 : null,
        bt_73: dateType === 'period' ? bt73 : null,
        bt_74: dateType === 'period' ? bt74 : null,
        bt_13: bt13 || null,
      });
      
      console.log('Update successful:', response.data);
      setXmlPreview(response.data.xml_preview);
      setDownloadFilename(response.data.download_filename);
      setValidation(response.data.validation || null);
      setIsUpdated(true);
      fetchBt13History();
      toast.success('Rechnung erfolgreich aktualisiert');
    } catch (error) {
      console.error('Update error:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'Fehler beim Aktualisieren der Rechnung';
      if (error.response?.status === 404) {
        errorMessage = 'Datei nicht gefunden. Bitte laden Sie die PDF erneut hoch.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (!fileId || !isUpdated) {
      return;
    }
    
    try {
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download gestartet');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Download');
    }
  };

  const resetForm = () => {
    setUploadedFile(null);
    setFileId(null);
    setOriginalFilename('');
    setXmlPreview('');
    setIsUpdated(false);
    setDownloadFilename('');
    setBt72('');
    setBt73('');
    setBt74('');
    setBt13('');
    setDateType('period');
    setValidation(null);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9FAFB' }}>
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl font-bold mb-2" 
            style={{ 
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: '-0.02em',
              color: '#0F172A'
            }}
          >
            ZUGFeRD Editor
          </h1>
          <p 
            className="text-base" 
            style={{ 
              fontFamily: 'Inter, sans-serif',
              color: '#64748B'
            }}
          >
            Bögl Zeugs austatten mit Lieferdatum und Leistungszeitraum in e-Rechnungen
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-12 gap-8">
          {/* Left Column: Upload & Form */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            {/* Upload Zone */}
            <div 
              className="bg-white rounded-lg p-6 border transition-all duration-200"
              style={{ 
                borderWidth: '1px',
                borderColor: '#E2E8F0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}
              data-testid="upload-section"
            >
              <h2 
                className="text-lg font-semibold mb-4" 
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  color: '#0F172A'
                }}
              >
                1. PDF hochladen
              </h2>
              
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className="relative transition-all duration-200"
                style={{
                  border: isDragging ? '2px dashed #0052CC' : '2px dashed #CBD5E1',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  padding: '48px 24px',
                  transform: isDragging ? 'scale(0.99)' : 'scale(1)'
                }}
                data-testid="upload-dropzone"
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-input"
                />
                
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload 
                    size={48} 
                    style={{ color: '#0052CC', marginBottom: '16px' }}
                  />
                  <p 
                    className="text-base font-medium mb-2" 
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      color: '#0F172A'
                    }}
                  >
                    {uploadedFile ? uploadedFile.name : 'PDF hier ablegen oder klicken'}
                  </p>
                  <p 
                    className="text-sm" 
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      color: '#64748B'
                    }}
                  >
                    Nur ZUGFeRD-konforme PDF-Dateien
                  </p>
                </div>
              </div>

              {uploadedFile && (
                <div 
                  className="mt-4 p-3 rounded flex items-center gap-3"
                  style={{ backgroundColor: '#F1F5F9' }}
                  data-testid="uploaded-file-info"
                >
                  <FileText size={20} style={{ color: '#0052CC' }} />
                  <div className="flex-1">
                    <p 
                      className="text-sm font-medium" 
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#0F172A'
                      }}
                    >
                      {originalFilename}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetForm}
                    data-testid="reset-button"
                  >
                    Neu
                  </Button>
                </div>
              )}
            </div>

            {/* Date Form */}
            {fileId && (
              <div 
                className="bg-white rounded-lg p-6 border"
                style={{ 
                  borderWidth: '1px',
                  borderColor: '#E2E8F0',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}
                data-testid="date-form-section"
              >
                <h2 
                  className="text-lg font-semibold mb-4" 
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    color: '#0F172A'
                  }}
                >
                  2. Datum eingeben
                </h2>

                <Tabs value={dateType} onValueChange={setDateType} className="w-full">
                  <TabsList className="w-full mb-6" data-testid="date-type-tabs">
                    <TabsTrigger value="period" className="flex-1" data-testid="period-tab">
                      <Calendar size={16} className="mr-2" />
                      Leistungszeitraum
                    </TabsTrigger>
                    <TabsTrigger value="delivery" className="flex-1" data-testid="delivery-tab">
                      <Calendar size={16} className="mr-2" />
                      Lieferdatum
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="period" className="space-y-4">
                    <div>
                      <Label 
                        htmlFor="bt73"
                        className="text-xs font-bold uppercase mb-2 block"
                        style={{ 
                          fontFamily: 'Inter, sans-serif',
                          letterSpacing: '0.05em',
                          color: '#64748B'
                        }}
                      >
                        BT-73: Startdatum
                      </Label>
                      <Input
                        id="bt73"
                        type="date"
                        value={bt73}
                        onChange={(e) => setBt73(e.target.value)}
                        className="w-full"
                        style={{
                          borderWidth: '1px',
                          borderColor: '#CBD5E1',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        data-testid="bt73-input"
                      />
                    </div>
                    <div>
                      <Label 
                        htmlFor="bt74"
                        className="text-xs font-bold uppercase mb-2 block"
                        style={{ 
                          fontFamily: 'Inter, sans-serif',
                          letterSpacing: '0.05em',
                          color: '#64748B'
                        }}
                      >
                        BT-74: Enddatum
                      </Label>
                      <Input
                        id="bt74"
                        type="date"
                        value={bt74}
                        onChange={(e) => setBt74(e.target.value)}
                        className="w-full"
                        style={{
                          borderWidth: '1px',
                          borderColor: '#CBD5E1',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        data-testid="bt74-input"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="delivery" className="space-y-4">
                    <div>
                      <Label 
                        htmlFor="bt72"
                        className="text-xs font-bold uppercase mb-2 block"
                        style={{ 
                          fontFamily: 'Inter, sans-serif',
                          letterSpacing: '0.05em',
                          color: '#64748B'
                        }}
                      >
                        BT-72: Lieferdatum
                      </Label>
                      <Input
                        id="bt72"
                        type="date"
                        value={bt72}
                        onChange={(e) => setBt72(e.target.value)}
                        className="w-full"
                        style={{
                          borderWidth: '1px',
                          borderColor: '#CBD5E1',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        data-testid="bt72-input"
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                {/* BT-13: Purchase Order Reference / Bestellreferenz */}
                <div className="mt-6 pt-6" style={{ borderTop: '1px solid #E2E8F0' }}>
                  <Label
                    htmlFor="bt13"
                    className="text-xs font-bold uppercase mb-2 block"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      letterSpacing: '0.05em',
                      color: '#64748B'
                    }}
                  >
                    BT-13: Bestellreferenz (Purchase Order Reference)
                  </Label>
                  <Input
                    id="bt13"
                    type="text"
                    value={bt13}
                    onChange={(e) => setBt13(e.target.value)}
                    placeholder="z.B. 4500123456"
                    className="w-full"
                    style={{
                      borderWidth: '1px',
                      borderColor: '#CBD5E1',
                      fontFamily: 'Inter, sans-serif'
                    }}
                    data-testid="bt13-input"
                  />
                  <p
                    className="text-xs mt-1"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      color: '#94A3B8'
                    }}
                  >
                    Optional. Wird in BuyerOrderReferencedDocument/IssuerAssignedID geschrieben.
                  </p>

                  {/* BT-13 history suggestions */}
                  {bt13History.length > 0 && (
                    <div className="mt-3" data-testid="bt13-history">
                      <div className="flex items-center gap-1 mb-2">
                        <History size={12} style={{ color: '#94A3B8' }} />
                        <span
                          className="text-xs"
                          style={{ fontFamily: 'Inter, sans-serif', color: '#94A3B8' }}
                        >
                          Zuletzt verwendet:
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {bt13History.map((s, idx) => (
                          <button
                            key={`${s.value}-${idx}`}
                            type="button"
                            onClick={() => setBt13(s.value)}
                            className="text-xs px-2.5 py-1 rounded-full transition-colors"
                            style={{
                              backgroundColor: bt13 === s.value ? '#0052CC' : '#F1F5F9',
                              color: bt13 === s.value ? '#FFFFFF' : '#0F172A',
                              fontFamily: 'Inter, sans-serif',
                              border: '1px solid #E2E8F0',
                              cursor: 'pointer'
                            }}
                            data-testid={`bt13-suggestion-${idx}`}
                            title={`Zuletzt benutzt: ${s.last_used?.slice(0, 10) || '-'}`}
                          >
                            {s.value}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex gap-3">
                  <Button
                    onClick={handleUpdateInvoice}
                    disabled={isProcessing}
                    className="flex-1"
                    style={{
                      backgroundColor: '#0052CC',
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: '500'
                    }}
                    data-testid="update-button"
                  >
                    {isProcessing ? 'Verarbeite...' : 'XML aktualisieren'}
                  </Button>
                  
                  {isUpdated && (
                    <Button
                      onClick={handleDownload}
                      style={{
                        backgroundColor: '#10B981',
                        color: '#FFFFFF',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: '500'
                      }}
                      data-testid="download-button"
                    >
                      <Download size={16} className="mr-2" />
                      Download
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Validation Result */}
            {isUpdated && validation && (
              <div
                className="bg-white rounded-lg p-4 border space-y-3"
                style={{
                  borderWidth: '1px',
                  borderColor: validation.xsd_valid && validation.schematron_valid ? '#10B981' : '#F59E0B',
                  backgroundColor: validation.xsd_valid && validation.schematron_valid ? '#F0FDF4' : '#FFFBEB'
                }}
                data-testid="validation-panel"
              >
                <div className="flex items-start gap-3">
                  {validation.xsd_valid && validation.schematron_valid ? (
                    <CheckCircle size={20} style={{ color: '#10B981', flexShrink: 0, marginTop: '2px' }} />
                  ) : (
                    <AlertTriangle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                  )}
                  <div className="flex-1">
                    <p
                      className="text-sm font-medium mb-2"
                      style={{ fontFamily: 'Inter, sans-serif', color: '#0F172A' }}
                    >
                      Rechnung erfolgreich aktualisiert
                    </p>
                    <p
                      className="text-xs mb-2"
                      style={{ fontFamily: 'Inter, sans-serif', color: '#64748B' }}
                    >
                      Datei zum Download bereit: <span className="font-mono">{downloadFilename}</span>
                    </p>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <span
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: validation.xsd_valid ? '#DCFCE7' : '#FEE2E2',
                          color: validation.xsd_valid ? '#166534' : '#991B1B',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        data-testid="xsd-status"
                      >
                        XSD: {validation.xsd_valid ? 'gültig' : 'ungültig'}
                      </span>
                      <span
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: validation.schematron_valid ? '#DCFCE7' : '#FEF3C7',
                          color: validation.schematron_valid ? '#166534' : '#92400E',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        data-testid="schematron-status"
                      >
                        Schematron (EN 16931): {validation.schematron_valid ? 'gültig' : `${validation.schematron_errors?.length || 0} Hinweise`}
                      </span>
                    </div>

                    {!validation.xsd_valid && validation.xsd_errors?.length > 0 && (
                      <div className="mt-3" data-testid="xsd-errors">
                        <p className="text-xs font-semibold mb-1" style={{ color: '#991B1B' }}>XSD-Fehler:</p>
                        <ul className="text-xs space-y-1" style={{ color: '#64748B', fontFamily: 'Inter, sans-serif' }}>
                          {validation.xsd_errors.slice(0, 5).map((err, i) => (
                            <li key={i} className="break-words">• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!validation.schematron_valid && validation.schematron_errors?.length > 0 && (
                      <details className="mt-3" data-testid="schematron-errors">
                        <summary
                          className="text-xs cursor-pointer font-medium"
                          style={{ color: '#92400E', fontFamily: 'Inter, sans-serif' }}
                        >
                          Schematron-Hinweise anzeigen ({validation.schematron_errors.length})
                        </summary>
                        <ul className="text-xs space-y-1 mt-2" style={{ color: '#64748B', fontFamily: 'Inter, sans-serif' }}>
                          {validation.schematron_errors.slice(0, 8).map((err, i) => (
                            <li key={i} className="break-words">• {err}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: XML Preview */}
          <div className="col-span-12 lg:col-span-5">
            <div className="lg:sticky lg:top-8">
              <div 
                className="bg-white rounded-lg p-6 border"
                style={{ 
                  borderWidth: '1px',
                  borderColor: '#E2E8F0',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}
                data-testid="xml-preview-section"
              >
                <h2 
                  className="text-lg font-semibold mb-4" 
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    color: '#0F172A'
                  }}
                >
                  XML-Vorschau
                </h2>
                
                {xmlPreview ? (
                  <div 
                    className="rounded-lg p-4 overflow-auto"
                    style={{
                      backgroundColor: '#1E1E1E',
                      color: '#D4D4D4',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '11px',
                      maxHeight: '600px',
                      borderWidth: '1px',
                      borderColor: '#374151',
                      lineHeight: '1.6'
                    }}
                    data-testid="xml-content"
                  >
                    <pre style={{ margin: 0 }}>{xmlPreview}</pre>
                  </div>
                ) : (
                  <div 
                    className="rounded-lg p-8 text-center"
                    style={{ backgroundColor: '#F8FAFC' }}
                  >
                    <FileText 
                      size={48} 
                      style={{ color: '#CBD5E1', margin: '0 auto 16px' }}
                    />
                    <p 
                      className="text-sm" 
                      style={{ 
                        fontFamily: 'Inter, sans-serif',
                        color: '#64748B'
                      }}
                    >
                      XML-Vorschau erscheint nach Upload
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p 
            className="text-xs" 
            style={{ 
              fontFamily: 'Inter, sans-serif',
              color: '#64748B'
            }}
          >
            Mirko baut einen ZUGFeRD Editor · EN 16931 konform und es läuft
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;